// src/encounter/lib/reported-date.ts
//
// How a patient-reported date leaves the rail's condition-onset and procedure-date fields.
//
// Encounter owner answers on PR #843 (1 Nov 2026), in
// `~/.claude/context/encounter-clinical-informatics-authority.md`:
//   · A duration ("3Y") is KEPT as an input aid, never stored as a computed full date. It is stored
//     at YEAR precision with the entered text and the date it was reported:
//     "about 2023, reported 1 Nov 2026 as 3 years ago". It is shown as "about 2023". No day or month
//     the patient did not give is ever shown.
//   · "Unknown" and blank stay different. Where a display-text slot exists, "Unknown" is written
//     there with no timestamp. Both rail forms have one: conditions `onsetDisplayDateTime`,
//     procedures `onsetDateTimeDisplay` (Scrips.Patient `CreatePatientProcedureRequest`).
//
// The year-precision timestamp is the first of the year at midnight, the same shape
// `DynamicDateInput` stores for a "YYYY" entry (`buildDate(year, 1, 1)` → `toIsoNoZ`).

/** The word written to the display-text slot for an explicitly unknown date. */
export const UNKNOWN_DATE_TEXT = 'Unknown';

/** What the date control holds: the typed text, what it resolved to, and the Unknown choice. */
export interface ReportedDateDraft {
  displayValue: string;
  isoValue: string | null;
  unknown: boolean;
  /** True once the user has changed the field or the Unknown choice. An untouched stored pair is
   *  sent back exactly as stored: a legacy duration ("3Y" with its old computed day) must not be
   *  re-derived against today on an edit that only changed the note (Codex review, PR #843). */
  edited: boolean;
}

export type ResolvedReportedDate = { ok: true; iso: string | null; display: string | null } | { ok: false };

/** Same grammar as `parseDynamicDate`'s duration branch, so what it accepts is what this catches. */
const DURATION = /^(\d{1,3})\s*([yYmMdD])$/;
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const UNIT_WORD: Record<string, string> = { Y: 'year', M: 'month', D: 'day' };
/** The stored duration text, from which only the "about YYYY" lead is shown. */
const STORED_DURATION = /^(about \d{4}), reported .+ ago$/;

function yearStartIso(year: number): string {
  return `${String(year).padStart(4, '0')}-01-01T00:00:00.000`;
}

/** "1 Nov 2026" — the LOCAL calendar day the entry was reported on. */
function reportDayText(d: Date): string {
  return `${d.getDate()} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/** The year `n` units before the local report day. */
function yearAgo(n: number, unit: string, reportedOn: Date): number {
  const d = new Date(reportedOn.getFullYear(), reportedOn.getMonth(), reportedOn.getDate());
  if (unit === 'Y') d.setFullYear(d.getFullYear() - n);
  else if (unit === 'M') d.setMonth(d.getMonth() - n);
  else d.setDate(d.getDate() - n);
  return d.getFullYear();
}

/** Seed the control from a stored record. A stored "Unknown" seeds the Unknown choice. */
export function seedReportedDate(display: string | null | undefined, iso: string | null | undefined): ReportedDateDraft {
  if ((display ?? '').trim().toLowerCase() === UNKNOWN_DATE_TEXT.toLowerCase()) {
    return { displayValue: '', isoValue: null, unknown: true, edited: false };
  }
  return { displayValue: display ?? '', isoValue: iso ?? null, unknown: false, edited: false };
}

/**
 * Resolve the control into the wire pair. `{ ok: false }` means typed text that cannot be read;
 * the caller shows its error rather than dropping what was typed.
 *
 * Blank sends no display text and the control's `isoValue` as it stands: a stored record can carry
 * a timestamp with no display text, and gating on the text would erase it on an unrelated edit.
 * Clearing the field already makes `isoValue` null (DynamicDateInput).
 */
export function resolveReportedDate(draft: ReportedDateDraft, reportedOn: Date = new Date()): ResolvedReportedDate {
  if (draft.unknown) return { ok: true, iso: null, display: UNKNOWN_DATE_TEXT };
  const typed = draft.displayValue.trim();
  if (!draft.edited) return { ok: true, iso: draft.isoValue, display: typed || null };
  if (!typed) return { ok: true, iso: draft.isoValue, display: null };
  const dur = DURATION.exec(typed);
  if (dur) {
    const n = Number(dur[1]);
    const unit = dur[2]!.toUpperCase();
    const year = yearAgo(n, unit, reportedOn);
    const word = `${UNIT_WORD[unit]}${n === 1 ? '' : 's'}`;
    return {
      ok: true,
      iso: yearStartIso(year),
      display: `about ${year}, reported ${reportDayText(reportedOn)} as ${n} ${word} ago`,
    };
  }
  if (!draft.isoValue) return { ok: false };
  return { ok: true, iso: draft.isoValue, display: typed };
}

/** What a reader is shown for stored display text: "about 2023" for a stored duration. */
export function shownReportedDate(display: string | null | undefined): string | null {
  const t = (display ?? '').trim();
  if (!t) return null;
  const m = STORED_DURATION.exec(t);
  return m ? m[1]! : t;
}

/** Display text that names a calendar day: DD/MM/YYYY, or an ISO date. */
const FULL_DAY = /^(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})$/;

/**
 * The label a chart reader shows for a stored date pair. Display text that is not a full calendar
 * day ("about 2023", "Unknown", "2019", "04/2021") is shown as text, because the timestamp beside
 * it is a first-of-period placeholder and formatting it would claim a day nobody recorded. A full
 * day, or no display text, goes through the reader's own full-date formatter.
 */
export function reportedDateLabel(
  iso: string | null | undefined,
  display: string | null | undefined,
  formatFullDate: (iso: string | null | undefined) => string,
): string {
  // A legacy duration ("3Y", saved before durations were stored at year precision) is relative to the day it was
  // entered, not to today: show the stored onset's year, never the bare relative text.
  if (DURATION.test((display ?? '').trim())) {
    const year = iso ? new Date(iso).getFullYear() : NaN;
    return Number.isFinite(year) ? `about ${year}` : formatFullDate(iso);
  }
  const shown = shownReportedDate(display);
  if (shown && !FULL_DAY.test(shown)) return shown;
  return formatFullDate(iso);
}
